/**
 * Manage state of modal window
 *
 * Usage:
 *   withModal(<ModalComponent />)
 *
 * The ModalComponent will be provided with "open" state and
 * two events: onClose and onOpen to manage state.
 *
 * @flow
 */

import React, { useState, useCallback, useRef, useEffect } from 'react';

export default function withModal(Component) {
    function WithModal(props) {
        const [open, setOpen] = useState(false);
        const mountedRef = useRef(true);

        useEffect(() => () => { mountedRef.current = false; }, []);

        const onClose = useCallback(() => {
            if (mountedRef.current) setOpen(false);
        }, []);

        const onOpen = useCallback(() => {
            if (mountedRef.current) setOpen(true);
        }, []);

        return <Component {...props} open={open} onClose={onClose} onOpen={onOpen} />;
    }

    WithModal.displayName = `withModal(${Component.displayName || Component.name || 'Component'})`;

    return WithModal;
}
